import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { X, Download } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PromptContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: ${props => props.theme.shadows.large};
  z-index: 9999;
  max-width: 400px;
  width: calc(100% - 40px);
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateX(-50%) translateY(100px);
      opacity: 0;
    }
    to {
      transform: translateX(-50%) translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    bottom: 10px;
    max-width: calc(100% - 20px);
  }
`;

const PromptContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PromptHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const PromptTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PromptDescription = styled.p`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.theme.colors.hover};
    color: ${props => props.theme.colors.text};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const InstallButton = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.colors.primaryHover};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(2, 132, 199, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const LaterButton = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  background: transparent;
  color: ${props => props.theme.colors.textSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.colors.hover};
    color: ${props => props.theme.colors.text};
    border-color: ${props => props.theme.colors.text};
  }
`;

export const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Проверяем, показывали ли уже промпт
    const hasShownPrompt = localStorage.getItem('pwa-install-prompt-shown');
    const promptDismissedAt = localStorage.getItem('pwa-install-prompt-dismissed');
    
    // Если пользователь отклонил меньше 7 дней назад, не показываем
    if (promptDismissedAt) {
      const dismissedDate = new Date(parseInt(promptDismissedAt));
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    const handler = (e: Event) => {
      // Предотвращаем автоматический промпт браузера
      e.preventDefault();
      
      // Сохраняем событие для последующего использования
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Показываем наш кастомный промпт через 3 секунды после загрузки
      // (чтобы не мешать пользователю сразу)
      if (!hasShownPrompt) {
        setTimeout(() => {
          setShowPrompt(true);
          localStorage.setItem('pwa-install-prompt-shown', 'true');
        }, 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    // Показываем нативный промпт браузера
    deferredPrompt.prompt();

    // Ждем выбор пользователя
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('✅ PWA установлено успешно!');
    } else {
      console.log('❌ Пользователь отклонил установку PWA');
    }

    // Очищаем состояние
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Сохраняем время отклонения
    localStorage.setItem('pwa-install-prompt-dismissed', Date.now().toString());
    console.log('ℹ️ Промпт установки PWA отложен на 7 дней');
  };

  // Не показываем промпт, если:
  // 1. Событие beforeinstallprompt не сработало (уже установлено или не поддерживается)
  // 2. Пользователь закрыл промпт
  if (!showPrompt || !deferredPrompt) {
    return null;
  }

  return (
    <PromptContainer>
      <PromptContent>
        <PromptHeader>
          <PromptTitle>
            <Download size={20} />
            Установите AI Assistant
          </PromptTitle>
          <CloseButton onClick={handleDismiss} aria-label="Закрыть">
            <X size={20} />
          </CloseButton>
        </PromptHeader>
        
        <PromptDescription>
          Установите приложение на главный экран для быстрого доступа и работы в офлайн-режиме!
        </PromptDescription>

        <ButtonGroup>
          <InstallButton onClick={handleInstall}>
            <Download size={18} />
            Установить
          </InstallButton>
          <LaterButton onClick={handleDismiss}>
            Позже
          </LaterButton>
        </ButtonGroup>
      </PromptContent>
    </PromptContainer>
  );
};

